import { EventEmitter } from 'eventemitter3';
import { EventTypes } from '../core/event-bus';
import { IData, IDataCoupon, IDataDeliveryZone, IDataItem, IDataServiceCharge, IDataStore, IDataTax, IMenuItemPrice, IMenuItemTax } from '../models/data.interface';
import { Util } from '../utils/util';
import { Data } from './data';
import { User } from '../models/user';
import { 
    IIncompleteDiscount, IOrder, IOrderChallengeMember, IOrderGiftCard, IOrderItem, IOrderItemModifier, 
    IOrderServiceChargeDetail, IOrderTax, IOrderCoupon } from '../models/order.interface';
import { OnlineOrderingUtil } from '../online-ordering/online-ordering-util';
import { CouponIdList, CouponTypeList, HalfModifierSelection, OrderType } from '../types/enums';
import { IGetItemTaxDependencies, IGetItemTaxoItem, IServiceChargeDetail } from '../online-ordering/interfaces/online-ordering.interface';
import { IApplyCouponByIdParams, IApplyCouponByIdParamsPartial, ICheckCouponHasQualifyingItemsParams, IRemovedCoupon, IvItem } from './coupons.interface';
import { Coupon } from './coupons';
import { ICouponState } from "../services/coupons/coupon-service.interface";
import { ILoyaltyProvider } from '../services/loyalty-provider/loyalty-provider.interface';
import { DataLayerService } from '../services/data-layer-service';
import { OnlineOrderingService } from '../services/online-ordering-service';
import { CouponService } from '../services/coupons/coupon-service';
import { IUserCampaignCodesData, IUserLoyaltyReward, IUserNextOrderCredit } from './user.interface';
import { ITempAddress } from '../interfaces/temp.interface';
import { Context } from './context';
import { LocalizationService } from '../services/localization-service';
import { GoogleMapsLoader } from '../services/maps/google/google-maps-loader';
import { ItemPricingUnavailableError } from '../online-ordering/errors/item-pricing-unavailable-error';
import { DialogCreators } from '../utils/dialog-creators';
import { TimedMessage } from '../components/shared/TimedMessage/timed-message';
import { getGeoServiceApiKeys } from '../services/geo-service-api-keys';

export class Order {
    private readonly _eventBus: EventEmitter;
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _couponService: CouponService;
    private readonly _dataLayer: DataLayerService;
    private readonly _localizationService: LocalizationService;
    private readonly _coupon: Coupon;
    private readonly _user: User;
    private readonly _incompleteDiscounts: IIncompleteDiscount[] = [];
    private readonly _legacyCouponCodeMsg: string = "Using legacy coupon code";
    private readonly _newCouponCodeMsg: string = "Using new coupon code service";
    private _order: IOrder;
    private _lastDeliveryAddress: { address: ITempAddress | null, storeKey: string | null } = { address: null, storeKey: null };

    constructor(eventBus: EventEmitter, data: Data, context: Context, localizationService: LocalizationService, user: User, dataLayer: DataLayerService, orderingService: OnlineOrderingService, couponService: CouponService) {
        this._eventBus = eventBus;
        this._data = data;
        this._context = context;
        this._couponService = couponService;
        this._dataLayer = dataLayer;
        this._localizationService = localizationService;
        this._user = user;
        this._coupon = new Coupon(eventBus, data, context, localizationService, user, dataLayer, orderingService);
        this._order = this._initiateOrder();
        this._eventBusListeners();
        this._couponService.setOrder(this._order);
    }

    public getProperty<K extends keyof IOrder>(property: K): IOrder[K] {
        if (!this._order)
            return this._getDefaultValue(property);

        return this._order[property];
    }

    public setProperty<K extends keyof IOrder>(key: K, value: IOrder[K]): void {
        this._order[key] = value;
    }

    public getIncompleteDiscounts(): IIncompleteDiscount[] {
        return this._incompleteDiscounts;
    }

    public getLastDeliveryAddress(): ITempAddress | null {
        return this._lastDeliveryAddress.address;
    }

    public getLastDeliveryAddressStore(): string | null {
        return this._lastDeliveryAddress.storeKey;
    }

    private readonly _eventBusListeners = (): void => {
        this._eventBus.on(EventTypes.ORDER_TYPE_CHANGED, this._orderTypeChanged);
        this._eventBus.on(EventTypes.DATA_RETRIEVED, this._storeDataChanged);
        this._eventBus.on(EventTypes.ORDER_COUPON_CHANGED, this._couponAddedOrRemoved);
        this._eventBus.on(EventTypes.CLEAR_CART, this._clearCart);
        this._eventBus.on(EventTypes.STORE_CHANGED, this._storeKeyChanged);
        //this._eventBus.on(EventTypes.ITEM_ADD, this._itemsAdded);
    }

    private readonly _storeKeyChanged = (storeKey: string, _loyProvider: ILoyaltyProvider, isCacheTrigger: boolean): void => {
        this._updateStoreKeyInLocalStorage(storeKey, isCacheTrigger);

        // Set StoreID synchronously here -- DATA_RETRIEVED's _storeDataChanged is the only other
        // setter, and its network fetch can otherwise leave StoreID stale at checkout (BUG 86565).
        const resolvedStoreId = this._data.locations.find(x => x.storeKey === storeKey)?.storeId;
        if (resolvedStoreId)
            this._order.StoreID = resolvedStoreId;
    }

    private readonly _storeDataChanged = async (loyaltyProvider: ILoyaltyProvider): Promise<void> => {
        const register = this._data.getProperty("Settings").STID;
        this._order.Key = this._getNewOrderKey(register);
        this._order.GUID = this._getNewOrderGUID(this._data.getProperty("StoreInfo").storeHex, register.toString(16));
        this._order.StoreID = this._data.getProperty("StoreInfo").storeId;

        this._orderTypeChangeLogic(this._order.OrderTypeID);

        await this._recalculateOrderPricesAsync(loyaltyProvider);
        await this.setOrderTypeChargeAsync();

        // Reset Donations if not exist
        this._resetDonations();

        //2DO: Check Cart items and store is still good.

        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    private readonly _orderTypeChanged = async (orderTypeId: OrderType, loyaltyProvider: ILoyaltyProvider): Promise<void> => {
        this._orderTypeChangeLogic(orderTypeId);

        await this._recalculateOrderPricesAsync(loyaltyProvider);
        await this.setOrderTypeChargeAsync();

        this._eventBus.emit(EventTypes.CART_CHANGED);

        // 2DO: Refactor When Call Center Functionality Added
        //if (this._order.OrderTypeID === OrderType.DELIVERY && this._data.getProperty("Settings").ISCC)
        //    await this.selectStoreForCheckedAddress();

        // 2DO: In Checkout screen
        //if (aOLO.Dialog.OrderType)
        //    aOLO.Dialog.OrderType.handleOrderTypeOrderTimeChange(true, false, true);

        //initializeOrderTypeGUI();
    }

    private _orderTypeChangeLogic(orderTypeId: OrderType): void {
        const orderTypeSubType = OnlineOrderingUtil.getOrderType(orderTypeId, this._data.getProperty("OrderTypeSubTypes"));
        if (!orderTypeSubType || orderTypeSubType.OrderTypeId === 0)
            return;

        this._order.OrderTypeID = orderTypeId;
        this._order.OrderTypeSubType = orderTypeSubType;

        this._handleDeliveryAddressCache(orderTypeId);

        // 2DO: Call Center
        // if (this._data.getProperty("Settings").ISCC && globalThis.aOLO.Temp.SelectedStoreID > 0) {
        //     const store = Util.getCallCenterStore(globalThis.aOLO.Temp.SelectedStoreID, this._data.getProperty("Stores"));
        //     if (store)
        //         this._order.WaitTime = OnlineOrderingUtil.getStoreWaitTime(store, orderTypeId, this._data.getProperty("TimeOffset"));
        // } else
        this._order.WaitTime = OnlineOrderingUtil.getWaitTimeByOrderType(this._data, orderTypeSubType);
    }

    private _handleDeliveryAddressCache(orderTypeId: OrderType): void {
        const isDelivery = orderTypeId === OrderType.DELIVERY;

        if (isDelivery) {
            if (this._order.CachedDeliveryAddress) {
                this.setAddress(this._order.CachedDeliveryAddress);
                this.setProperty("CachedDeliveryAddress", null);
            }
        } else {
            if (this._order.Address && !this._order.CachedDeliveryAddress) {
                this.setProperty("CachedDeliveryAddress", this._order.Address);
            }
            this.setAddress(null);
        }
    }

    private readonly _couponAddedOrRemoved = (): void => {
        for (const oItem of this._order.Items) {
            const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId) || null;
            OnlineOrderingUtil.TaxItem(mItem, oItem, this.getItemTaxDependencies());
        }
        this.TaxOrder();
        this.calculateTotals();
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public readonly getAmountOfItemsInCart = (): number => {
        const items = this._order.Items.filter(x => [0, 1].includes(x.HalfIndex));
        return items.reduce((total, x) => total + x.Quantity, 0);
    }

    // Prices and adds items to the cart. Returns false (and shows the user an error) without
    // modifying the cart if any item can't be priced -- callers must skip their success messaging.
    public readonly addItems = async (loyaltyProvider: ILoyaltyProvider, items: IOrderItem[]): Promise<boolean> => {
        const deps = this.getItemTaxDependencies();
        const pgId = this.getProperty("OrderTypeSubType").PriceGroupId;

        // None of the items are added if any one fails to price.
        try {
            for (const item of items) {
                OnlineOrderingUtil.PriceItem(this._data, items, item, deps, pgId);
            }
        } catch (e) {
            if (!(e instanceof ItemPricingUnavailableError))
                throw e;

            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayer,
                this._localizationService.names("ITEM_PRICING_UNAVAILABLE"),
                true
            );
            return false;
        }

        // Remove existing items with matching ItemKeys
        const itemKeysToReplace = new Set(items.map(i => i.ItemKey));
        this._order.Items = this._order.Items.filter(i => !itemKeysToReplace.has(i.ItemKey));

        // Add new items
        this._order.Items.push(...items);

        // Sort by ItemKey and then ItemRecId
        this._order.Items.sort((a, b) =>
            a.ItemKey - b.ItemKey || a.ItemRecId - b.ItemRecId
        );

        this.TaxOrder();
        this.calculateTotals();

        // BUG: 68869 - remove all Percentage Off Entire Order coupons to re-apply after to allow coupons to apply to the new items.
        const couponParamsToReapply: IApplyCouponByIdParams[] = await this._processCouponsToApplyLastAsync(loyaltyProvider);
        for (const couponParams of couponParamsToReapply) {
            await this.applyCouponByIdAsync(couponParams);
        }

        this._saveCartToLocalStorage(items.map(i => i.ItemKey), undefined, true, false);
        this._eventBus.emit(EventTypes.CART_CHANGED);
        return true;
    }

    public removeItemByItemRecId(itemRecId: number): void {
        const itemKeysToRemove = this._order.Items.filter(x => x.ItemRecId === itemRecId).map(i => i.ItemKey);
        this._order.Items = this._order.Items.filter(x => x.ItemRecId !== itemRecId);
        this._saveCartToLocalStorage(itemKeysToRemove, undefined, false, true);
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public addGiftCard = (giftCard: IOrderGiftCard): void => {
        this._order.GiftCards.push(giftCard);
        this.calculateTotals();
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public removeGiftCard = (cardNumber: string): void => {
        this._order.GiftCards = this._order.GiftCards.filter(x => x.cardNo !== cardNumber);
        this.calculateTotals();
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public removeAllGiftCards = (): void => {
        this._order.GiftCards = [];
        this.calculateTotals();
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public setAsapTime = (): void => {
        const timeOffset = this._data.getProperty("TimeOffset");
        const businessStartMinute = this._data.getProperty("Settings").BSM;
        this._order.BUSDate = Util.formatDate(Util.GetBUSDate(Util.NowStore(timeOffset), timeOffset, businessStartMinute));
        this._order.FutureDate = null;
        this._order.FutureMinute = -1;
        this._eventBus.emit(EventTypes.TIME_CHANGED);
    }

    public setFutureDate = (date: Date, time: number): void => {
        // PromiseTime is FutureDate + FutureMinute, so FutureDate must be midnight; callers may pass a BSM-shifted business date.
        const futureDate = new Date(date);
        futureDate.setHours(0, 0, 0, 0);

        this._order.BUSDate = Util.formatDate(futureDate, "en-US");
        this._order.FutureDate = futureDate;
        this._order.FutureMinute = time;
        this._eventBus.emit(EventTypes.TIME_CHANGED);
    }

    public setOrderType(orderTypeId: OrderType, loyaltyProvider: ILoyaltyProvider): void {
        this._order.OrderTypeID = orderTypeId;
        this._eventBus.emit(EventTypes.ORDER_TYPE_CHANGED, orderTypeId, loyaltyProvider);
    }

    /**
     * Sets the address for the order.
     * If `null` is passed, it clears the address.
     * @param {ITempAddress | null} address - The address object to set, or `null` to clear the address.
     */
    public setAddress(address: ITempAddress | null): void {
        if (this._order.Address)
            this._lastDeliveryAddress = {
                address: this._order.Address,
                storeKey: this._data.storeKey
            };

        if (address) {
            this._order.Address = address;
            this._order.AddressID = address.AddressID
        } else {
            this._order.Address = null;
            this._order.AddressID = 0;
        }
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    private _getDefaultValue<K extends keyof IOrder>(property: K): IOrder[K] {
        // Fallback type map for default values
        const fallbackTypes: { [key in keyof IOrder]: IOrder[key] } = {
            AddressID: 0,
            Address: null,
            AmountDue: 0,
            BUSDate: null,
            Challenges: [],
            CollectedTax: 0.0,
            Comment: "",
            Coupons: [],
            Discount: 0.0,
            Distance: 0,
            Donations: { Detail: [] },
            FutureDate: null,
            FutureMinute: -1,
            Fundraiser: null,
            FundraiserID: 0,
            GiftCards: [],
            GUID: "",
            IsTaxExempt: false,
            Items: [],
            ItemsCoupons: [],
            ItemsMakeList: [],
            Key: "",
            OrderID: 0,
            OrderNo: 0,
            OrderTypeCharge: 0.0,
            OrderTypeSubID: 7,
            OrderTypeID: 0,
            OrderStartTime: new Date(),
            OrderEndTime: null,
            OrderTypeSubType: {
                OrderTypeId: 0,
                OrderTypeSubId: 0,
                PriceGroupId: 0,
                Alias: "",
                AskCustomerInfo: false,
                AskCustomerAddress: false,
                DefaultPay: 0,
                EndMinute: 0,
                FutureOrders: false,
                MinAmount: 0,
                Names: [],
                OrderTypeCharge: 0,
                OrderTypeChargeTaxId: 0,
                PausedUntil: null,
                Rate: 0,
                StartMinute: 0,
                TaxTypeId: 0,
                TaxNames: [],
                WaitTime: 0
            },
            Payments: [],
            PrintTime: null,
            PromiseTime: null,
            ServiceCharges: {
                Amount: 0.00,
                Detail: [],
                Tax: 0.0
            },
            StoreID: 0,
            SubTotal: 0.0,
            Tax: 0.0,
            Taxes: [],
            TaxesDetail: [],
            Tip: 0.0,
            Total: 0.0,
            TotalDonation: 0.0,
            TaxInSubTotal: 0.0,
            WaitTime: 0
        };

        return fallbackTypes[property];
    }

    public readonly startNewOrder = (fromConfirmation: boolean = false): void => {
        this._clearCart();
        this._order = this._initiateOrder();
        this._couponService.setOrder(this._order);

        if (fromConfirmation)
            this._data.clearStoreKey();

        this._eventBus.emit(EventTypes.CART_CHANGED, fromConfirmation);
    }

    private readonly _initiateOrder = (): IOrder => {
        const register = this._data.getProperty("Settings").STID;

        return {
            AddressID: 0,
            Address: null,
            AmountDue: 0,
            BUSDate: null,
            Challenges: [],
            CollectedTax: 0,
            Comment: "",
            Coupons: [],
            Discount: 0,
            Distance: 0,
            Donations: { Detail: [] },
            FutureDate: null,
            FutureMinute: -1,
            Fundraiser: null,
            FundraiserID: 0,
            GiftCards: [],
            GUID: this._getNewOrderGUID(this._data.getProperty("StoreInfo").storeHex, register.toString(16)),
            IsTaxExempt: false,
            Items: [],
            ItemsCoupons: [],
            ItemsMakeList: [],
            Key: this._getNewOrderKey(register),
            OrderID: 0,
            OrderNo: 0,
            OrderTypeCharge: 0,
            OrderTypeSubID: 7, // online
            OrderTypeID: this._context.getProperty("defaultOrderType"),
            OrderStartTime: new Date(),
            OrderEndTime: null,
            OrderTypeSubType: {
                OrderTypeId: 0,
                OrderTypeSubId: 0,
                PriceGroupId: 0,
                Alias: "",
                AskCustomerInfo: false,
                AskCustomerAddress: false,
                DefaultPay: 0,
                EndMinute: 0,
                FutureOrders: false,
                MinAmount: 0,
                Names: [],
                OrderTypeCharge: 0,
                OrderTypeChargeTaxId: 0,
                PausedUntil: null,
                Rate: 0,
                StartMinute: 0,
                TaxTypeId: 0,
                TaxNames: [],
                WaitTime: 0
            },
            Payments: [],
            PrintTime: null,
            PromiseTime: null,
            ServiceCharges: {
                Amount: 0,
                Detail: [],
                Tax: 0
            },
            StoreID: this._data.getProperty("StoreInfo").storeId,
            SubTotal: 0,
            Tax: 0,
            Taxes: [],
            TaxesDetail: [],
            Tip: 0,
            Total: 0,
            TotalDonation: 0,
            TaxInSubTotal: 0,
            WaitTime: 0
        };
    }

    private readonly _getNewOrderGUID = (storeX: string, registerX: string): string => {
        function S4() {
            return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
        }
        let s8 = (storeX + S4() + S4()).substring(0, 8);
        let r4 = (registerX + S4()).substring(0, 4);
        return (s8 + "-" + S4() + "-4" + S4().substring(0, 3) + "-" + r4 + "-" + S4() + S4() + S4()).toLowerCase();
    }

    private readonly _getNewOrderKey = (register: number): string => {
        try {
            let rnd = "7";
            rnd = rnd + Math.random().toString().replace(".", "");
            rnd = Util.Right(rnd, 1, "6");
            return Util.Right(register.toString().trim(), 7, "0") + rnd + Util.Right((parseInt((new Date().getTime() / 10).toString()).toString()), 11, "0");
        } catch (ex: unknown) {
            if (ex instanceof Error) {
                //Util.LogError("GetNewOrderKey", ex, this._aOLO);
            }
        }
        return "";
    }

    public readonly createBaseOrderItem = (): IOrderItem => {
        const oItem: IOrderItem = {
            ItemId: 0,
            Name: "",
            Index: 0,
            SizeId: 0,
            SizeNames: "",
            Quantity: 1,
            Modifiers: [],
            Price: 0,
            MinPrice: 0,
            HalfHalfPrice: 0,
            MenuPrice: 0,
            MenuPriceTax: 0,
            Tax: 0,
            PriceIncludesTax: false,
            Taxes: [],
            TaxAmount: 0,
            BeforeTaxDiscount: 0,
            AfterTaxDiscount: 0,
            LoyaltyDiscountsQTY: 0,
            NonLoyaltyDiscountsQTY: 0,
            LoyaltyDiscountAmount: 0,
            DiscountedMarkedQuantity: 0,
            DiscountedQuantity: 0,
            ItemWebCategoryId: 0,
            ItemCategoryId: 0,
            ItemKey: -1,
            ItemRecId: 0,
            HalfCount: 1,
            HalfIndex: 0,
            Edit: false,
            Comment: "",
            IsBuildYourOwn: false,
            IsRedeemed: false,
            Instructions: []
        };
        return oItem;
    }

    public getNextItemKey(): number {
        return this._order.Items.reduce((maxIndex: number, oItem: IOrderItem) => {
            return Math.max(maxIndex, oItem.ItemKey);
        }, 0) + 1;
    }

    public getItemRecordId(itemRecIds?: number[]): number {
        const items = this._order.Items.map(x => x.ItemRecId);
        if (itemRecIds)
            items.push(...itemRecIds);

        const maxRecId = items.reduce((max: number, itemRecId: number) => {
            if (itemRecId > max)
                return itemRecId;
            return max;
        }, 99);
        return maxRecId + 1;
    }

    public calculateTotals(): void {
        this.TaxOrder();

        this._order.SubTotal = 0;
        this._order.Discount = 0;
        this._order.Tax = 0;

        this._order.TaxInSubTotal = this.fGetTaxInSubTotal();
        this._order.SubTotal = this.fGetSubTotal();

        for (const coupon of this._order.Coupons)
            this._order.Discount = Util.Float2(this._order.Discount + coupon.Amount);

        this.fSetServiceCharges();

        for (const tax of this._order.Taxes)
            this._order.Tax += tax.Amount;

        this._order.CollectedTax = Util.Float2(this._order.Tax);

        this._order.Total = this.calculateOrderTotal();
        this._order.AmountDue = this._order.Total;

        this.CalculateGiftcardsInTotals();
    }

    public CalculateGiftcardsInTotals(): void {
        if (this._order.GiftCards.length === 0)
            return;

        let orderAmountDue = Util.Float2(this._order.AmountDue);
        for (const giftCard of this._order.GiftCards) {
            const gCard = giftCard;
            if (gCard.balance > 0) {
                if (gCard.balance > orderAmountDue)
                    gCard.appliedAmount = orderAmountDue;
                else
                    gCard.appliedAmount = Util.Float2(gCard.balance);
                orderAmountDue -= Util.Float2(gCard.appliedAmount);
            }
        }

        this._order.AmountDue = Util.Float2(orderAmountDue);
        this._eventBus.emit(EventTypes.GIFT_CARD_CHANGED);
    }

    public calculateOrderTotal(): number {
        return Util.Float2(
            Util.Float2(this._order.SubTotal)
            - Util.Float2(this._order.Discount)
            + Util.Float2(this._order.OrderTypeCharge)
            + Util.Float2(this._order.CollectedTax)
            + Util.Float2(this._order.Tip)
            + Util.Float2(this._order.ServiceCharges.Amount)
            + Util.Float2(this._order.TotalDonation)
            - Util.Float2(this._order.TaxInSubTotal)
        );
    }

    public fGetTaxInSubTotal(): number {
        let subTotalTaxInc = 0;
        const items = this._order.Items.filter(x => x.PriceIncludesTax);

        for (const oItem of items)
            subTotalTaxInc += Util.Float2(oItem.Tax);

        return Util.Float2(subTotalTaxInc);
    }

    public fGetSubTotal(): number {
        let subTotal = 0;

        for (const oItem of this._order.Items) {
            // skip the right half of any half-and-half
            if (oItem.HalfCount === 2 && oItem.HalfIndex === HalfModifierSelection.RIGHT) {
                continue;
            }

            // otherwise whole items (HalfCount===1) or left halves (HalfIndex===LEFT) add them
            subTotal += Util.Float2((oItem.HalfCount == 2 ? oItem.HalfHalfPrice : oItem.Price) * oItem.Quantity);
        }

        return Util.Float2(subTotal);
    }

    public fSetServiceCharges(): void {
        // Initialize and clear old values 
        this._order.ServiceCharges.Tax = 0;
        this._order.ServiceCharges.Amount = 0;
        this._order.ServiceCharges.Detail = [];

        const serviceCharges = this._data.getProperty("ServiceCharges") || [];

        if (serviceCharges.length === 0)
            return;

        const orderServiceCharges: IServiceChargeDetail = {
            Tax: 0,
            Amount: 0,
            Detail: []
        };

        for (const sc of serviceCharges) {
            const amount = this.GetServiceChargeAmount(sc) || 0;
            if (amount <= 0)
                continue;

            const tax = (sc.TaxId > 0 && sc.TaxRate) ? Util.Float5(amount * sc.TaxRate / 100) : 0;

            const serChrg: IOrderServiceChargeDetail = {
                ID: sc.ServiceChargeId,
                Name: this._localizationService.GetName(sc.Names),
                Names: sc.Names,
                Amount: amount,
                Overwritten: false,
                Tax: tax,
                TaxID: sc.TaxId,
                TaxTID: sc.TaxTypeId,
                TaxNAM: sc.TaxName
            };

            orderServiceCharges.Amount += amount;
            orderServiceCharges.Tax += tax;
            orderServiceCharges.Detail.push(serChrg);
        }

        let totalTax = 0;
        let totalCharge = 0;

        for (const serviceCharge of orderServiceCharges.Detail) {
            const sch = serviceCharge;
            const oSch = this.GetExistingServiceCharge(sch.ID);
            if (oSch) {
                if (!oSch.Overwritten) {
                    totalCharge += sch.Amount;
                    totalTax += sch.Tax;
                    oSch.Amount = sch.Amount;
                    oSch.Tax = sch.Tax
                } else {
                    totalCharge += oSch.Amount;
                    totalTax += oSch.Tax;
                }
            } else {
                totalCharge += sch.Amount;
                totalTax += sch.Tax;
                this._order.ServiceCharges.Detail.push(sch);
            }
        }

        this._order.ServiceCharges.Amount = totalCharge;
        this._order.ServiceCharges.Tax = totalTax;
        this.TaxOrder();
    }

    /**
     * Resets the donation list by removing any donations that are not available in the store's donation options.
     */
    private _resetDonations(): void {
        const availableDonations = this._data.getProperty("Donations").map(x => x.DonationId);

        for (let i = this._order.Donations.Detail.length - 1; i >= 0; i--) {
            const donation = this._order.Donations.Detail[i];
            if (!availableDonations.includes(donation.ID))
                this._order.Donations.Detail.splice(i, 1);
        }

        this._calculateTotalDonation();
    }

    /**
     * Calculates the total donation amount from the donation list and updates the order total.
     */
    private _calculateTotalDonation(): void {
        const totalDonation = this._order.Donations.Detail.reduce((sum, detail) => sum + detail.Amount, 0);
        this._order.TotalDonation = totalDonation;
    }

    /**
     * Creates a new donation entry with an initial amount of 0.
     * If the donation already exists, it resets the amount to 0.
     * @param {number} donationId - The ID of the donation to create.
     */
    public createDonation(donationId: number): void {
        const donation = this._order.Donations.Detail.find(x => x.ID === donationId);
        if (donation)
            donation.Amount = 0;
        else
            this._order.Donations.Detail.push({ ID: donationId, Amount: 0 });

        this._calculateFinalDonations();
    }

    /**
     * Updates the amount of a specified donation and recalculates the total donation amount.
     * Emits an event to indicate the cart has changed.
     * @param {number} donationId - The ID of the donation to update.
     * @param {number} amount - The new donation amount.
     */
    public updateDonationAmount(donationId: number, amount: number): void {
        const donation = this._order.Donations.Detail.find(x => x.ID === donationId);
        if (donation)
            donation.Amount = amount;

        this._calculateFinalDonations();
    }

    private _calculateFinalDonations(): void {
        this._calculateTotalDonation();
        this.calculateTotals();
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public GetServiceChargeAmount(serviceCharge: IDataServiceCharge): number | null {
        let chargeableAmount = 0;
        if (serviceCharge.IncludesSubtotal)
            chargeableAmount += this._order.SubTotal;

        if (serviceCharge.IncludesDeliveryCharge)
            chargeableAmount += this._order.OrderTypeCharge;

        if (serviceCharge.IncludesTax)
            chargeableAmount += (this.fGetOrderTypeChargeTaxes() + this.fGetOrderItemTaxes());

        if (serviceCharge.MinAmount && chargeableAmount < serviceCharge.MinAmount)
            return null;
        if (serviceCharge.MaxAmount && chargeableAmount > serviceCharge.MaxAmount)
            return null;

        const otst = serviceCharge.OrderTypeSubTypes?.find(otst => otst.OrderTypeId === this._order.OrderTypeSubType?.OrderTypeId && otst.OrderTypeSubId === this._order.OrderTypeSubType?.OrderTypeSubId);
        if (!otst)
            return null;

        let chargeAmount = serviceCharge.FixAmount + (chargeableAmount * serviceCharge.Percentage) / 100;
        if (chargeAmount < serviceCharge.MinCharge)
            chargeAmount = serviceCharge.MinCharge;

        if (chargeAmount > serviceCharge.MaxCharge && serviceCharge.MaxCharge !== 0)
            chargeAmount = serviceCharge.MaxCharge;

        return chargeAmount;
    }

    public fGetOrderItemTaxes(): number {
        let totalTax = 0;

        for (const item of this._order.Items) {
            if (item.IsRemoved) {
                item.Taxes = [];
            } else {
                for (const tax of item.Taxes) {
                    totalTax += Util.Float5(tax.Amount);
                }
            }
        }

        return totalTax;
    }

    public fGetOrderTypeChargeTaxes(): number {
        if (this._order.OrderTypeCharge + this._order.SubTotal - this._order.Discount <= 0 || this._order.IsTaxExempt)
            return 0;

        const orderTypeCharge = this._order.OrderTypeCharge + Math.min(0, this._order.SubTotal - this._order.Discount);

        let taxRate = Util.Float5(this._order.OrderTypeSubType.Rate);
        if (this._data.getProperty("Settings")?.ISCC) {
            const stores = this._data.getProperty("Stores") || [];
            const storeTax = this._getCallCenterStoreTaxRate(this._order.OrderTypeSubType.OrderTypeChargeTaxId, this._order.StoreID, stores);
            if (storeTax)
                taxRate = storeTax.taxRate;
        }

        if (this._order.OrderTypeID === OrderType.DELIVERY && this._order.Address) {
            const zip = this._order.Address.Zip;
            if (zip) {
                const zipTaxes = this._data.getProperty("ZipTaxes") || [];
                const zipTax = OnlineOrderingUtil.GetZipTaxes(this._order.OrderTypeSubType.OrderTypeChargeTaxId, zip, zipTaxes);
                if (zipTax !== null)
                    taxRate = zipTax.Rate;
            }
        }

        return Util.Float5(orderTypeCharge * taxRate / 100);
    }

    private _getCallCenterStoreTaxRate(taxId: number, storeId: number, stores: IDataStore[]): { taxAmount: number, taxRate: number, sizeID: number } | null {
        const store = Util.getCallCenterStore(storeId, stores);
        const tax = store?.Taxes.find(tax => tax.TaxId === taxId);
        if (tax)
            return {
                taxAmount: Util.Float5(tax.FixedAmount),
                taxRate: Util.Float5(tax.Rate),
                sizeID: tax.SizeId
            };
        return null;
    }

    private GetExistingServiceCharge(serviceChargeId: number): IOrderServiceChargeDetail | null {
        return this._order.ServiceCharges.Detail.find(sc => sc.ID === serviceChargeId) || null;
    }

    public GetItemByItemKey(itemKey: number): IOrderItem | null {
        return this._order.Items.find(x => x.ItemKey == itemKey) || null;
    }

    public addIncompleteDiscount = (loyaltyProvider: ILoyaltyProvider, incompleteCoupon: IIncompleteDiscount): void => {
        if (this._incompleteDiscounts.some(cpn => cpn.couponId === incompleteCoupon.couponId))
            return;

        this._incompleteDiscounts.push({
            couponId: incompleteCoupon.couponId,
            couponCode: incompleteCoupon.couponCode,
            channelId: incompleteCoupon.channelId,
            isCampaignCode: incompleteCoupon.isCampaignCode,
            isPromotionCode: incompleteCoupon.isPromotionCode,
            rewardId: incompleteCoupon.rewardId || null,
            isBankedCurrency: incompleteCoupon.isBankedCurrency,
            sourceId: incompleteCoupon.sourceId,
            isSingleUseCoupon: incompleteCoupon.isSingleUseCoupon
        });

        loyaltyProvider.setIncompleteReward(incompleteCoupon.rewardId || null);
    }

    public TaxOrder = (): void => {
        const orderTaxes: IOrderTax[] = [];
        let orderTaxesDetail: IOrderTax[] = [];
        for (const oItem of this._order.Items) {
            let excludeTax = false;

            for (const tax of oItem.Taxes) {
                const taxData = this._data.getProperty("Taxes").find(x => x.TaxId === tax.TaxId);
                if (taxData && taxData.OrderTypesExcluded.includes(this._order.OrderTypeID)) {
                    if (tax.Amount > 0)
                        tax.Amount = 0;
                    excludeTax = true;
                    continue;
                }

                const itemTax = oItem.PriceIncludesTax ? Util.Float5(oItem.Tax) : Util.Float5(tax.Amount);
                const orderTax = orderTaxes.find(x => x.TaxTypeId == tax.TaxTypeId);
                if (!orderTax) {
                    const oTax = {
                        DisplayNextToItem: tax.DisplayNextToItem,
                        Amount: itemTax,
                        TaxTypeId: tax.TaxTypeId,
                        TaxTypeName: tax.TaxTypeName
                    } as IOrderTax;
                    orderTaxes.push(oTax);
                } else
                    orderTax.Amount = Util.Float5(orderTax.Amount + Util.Float5(itemTax));

                // Tax Detail
                const taxDef: IDataTax | undefined = this._getTaxDefinitionById(tax.TaxId, this._data.getProperty("Taxes"));
                if (taxDef) {
                    taxDef.Names = typeof taxDef.Names === 'string' ? JSON.parse(taxDef.Names) : taxDef.Names;
                    const itemTaxDetail = oItem.PriceIncludesTax ? Util.Float5(oItem.Tax) : Util.Float5(tax.Amount);
                    const orderTaxDetail = orderTaxesDetail.find(x => x.TaxId == tax.TaxId);
                    if (!orderTaxDetail) {
                        const oTax = {
                            DisplayNextToItem: tax.DisplayNextToItem,
                            Amount: itemTaxDetail,
                            TaxTypeId: tax.TaxTypeId,
                            TaxId: tax.TaxId,
                            TaxTypeName: taxDef.Names
                        } as IOrderTax;
                        orderTaxesDetail.push(oTax);
                    } else
                        orderTaxDetail.Amount += Util.Float5(itemTaxDetail);
                }
            }
            if (excludeTax)
                oItem.TaxAmount = 0;
        }

        if (this._order.OrderTypeCharge > 0) {
            const orderTypeChargeTax = this._getOrderTypeChargeTax();

            const orderTax = orderTaxes.find(x => x.TaxTypeId == this._order.OrderTypeSubType.TaxTypeId);
            if (!orderTax && this._order.OrderTypeSubType.TaxTypeId) {
                const oTax = {
                    DisplayNextToItem: false,
                    Amount: orderTypeChargeTax,
                    TaxTypeId: this._order.OrderTypeSubType.TaxTypeId,
                    TaxTypeName: this._order.OrderTypeSubType.TaxNames
                } as IOrderTax;
                orderTaxes.push(oTax);
            } else if (orderTax)
                orderTax.Amount += Util.Float5(orderTypeChargeTax);

            // Tax Detail
            const orderTypeChargeTaxId = this._order.OrderTypeSubType.OrderTypeChargeTaxId;
            const taxDef: IDataTax | undefined = this._getTaxDefinitionById(orderTypeChargeTaxId, this._data.getProperty("Taxes"));
            if (taxDef) {
                taxDef.Names = typeof taxDef.Names === 'string' ? JSON.parse(taxDef.Names) : taxDef.Names;
                const orderTaxDetail = orderTaxesDetail.find(x => x.TaxId == orderTypeChargeTaxId);
                if (!orderTaxDetail && this._order.OrderTypeSubType.TaxTypeId) {
                    const oTax = {
                        DisplayNextToItem: false,
                        Amount: orderTypeChargeTax,
                        TaxTypeId: this._order.OrderTypeSubType.TaxTypeId,
                        TaxId: orderTypeChargeTaxId,
                        TaxTypeName: taxDef.Names
                    } as IOrderTax;
                    orderTaxesDetail.push(oTax);
                } else if (orderTaxDetail)
                    orderTaxDetail.Amount += Util.Float5(orderTypeChargeTax);
            }
        }

        if (this._order.ServiceCharges?.Detail.length > 0) {
            for (const serviceCharge of this._order.ServiceCharges.Detail) {
                if (serviceCharge.Tax <= 0)
                    continue;

                const orderTax = orderTaxes.find(x => x.TaxTypeId == serviceCharge.TaxTID);
                if (!orderTax) {
                    const oTax = {
                        DisplayNextToItem: false,
                        Amount: serviceCharge.Tax,
                        TaxTypeId: serviceCharge.TaxTID,
                        TaxTypeName: serviceCharge.Names
                    } as IOrderTax;
                    orderTaxes.push(oTax);
                } else {
                    orderTax.Amount += Util.Float5(serviceCharge.Tax);
                }

                // Tax Detail
                const orderTaxDetail = orderTaxesDetail.find(x => x.TaxId == serviceCharge.TaxID);
                if (!orderTaxDetail) {
                    const taxDef: IDataTax | undefined = this._getTaxDefinitionById(serviceCharge.TaxID, this._data.getProperty("Taxes"));
                    if (taxDef) {
                        taxDef.Names = typeof taxDef.Names === 'string' ? JSON.parse(taxDef.Names) : taxDef.Names;
                        const oTax = {
                            DisplayNextToItem: false,
                            Amount: serviceCharge.Tax,
                            TaxTypeId: serviceCharge.TaxTID,
                            TaxId: serviceCharge.TaxID,
                            TaxTypeName: serviceCharge.Names
                        } as IOrderTax;
                        orderTaxesDetail.push(oTax);
                    }
                } else {
                    orderTaxDetail.Amount += Util.Float5(serviceCharge.Tax);
                }
            }
        }

        // Sorting Tax Detail array by TaxTypeName
        orderTaxesDetail = orderTaxesDetail.sort((a, b) => {
            const taxNameA: string | undefined = a.TaxTypeName?.find(x => x.CULT === this._context.getProperty("languageCode"))?.NAM;
            const taxNameB: string | undefined = b.TaxTypeName?.find(x => x.CULT === this._context.getProperty("languageCode"))?.NAM;
            if (taxNameA && taxNameB)
                return taxNameA.localeCompare(taxNameB);

            if (taxNameA)
                return 1;

            if (taxNameB)
                return -1;
            return 0;
        });

        this._order.Taxes = orderTaxes;
        this._order.TaxesDetail = orderTaxesDetail;
    }

    private _getOrderTypeChargeTax = (): number => {
        const taxRate = this._getTaxRate();
        return (taxRate > 0) ? (this._order.OrderTypeCharge * taxRate * 0.01) : 0;
    }

    private _getTaxRate = (): number => {
        let taxRate = Util.Float5(this._order.OrderTypeSubType.Rate);
        if (this._data.getProperty("Settings").ISCC) {
            const storeTax = this._getCallCenterStoreTaxRate(this._order.OrderTypeSubType.OrderTypeChargeTaxId, this._order.StoreID, this._data.getProperty("Stores"));
            if (storeTax)
                taxRate = storeTax.taxRate;
        }

        if (this._order.OrderTypeID === OrderType.DELIVERY) {
            if (this._order.Address) {
                const zipTax = OnlineOrderingUtil.GetZipTaxes(this._order.OrderTypeSubType.OrderTypeChargeTaxId, this._order.Address.Zip, this._data.getProperty("ZipTaxes"));
                if (zipTax)
                    taxRate = Util.Float5(zipTax.Rate);
            }
        }

        return taxRate;
    }

    public deleteItemByItemKeyAsync = async (itemKey: number, loyaltyProvider: ILoyaltyProvider): Promise<void> => {
        let item: IOrderItem | null = null;
        let discountedQuantity = 0;
        let couponsToApplyAfterTotals: IRemovedCoupon[] = [];

        const items = this._order.Items
        // Iterate through the items backward to safely remove elements
        for (let i = items.length - 1; i >= 0; i--) {
            const oItem = items[i];
            if (oItem.ItemKey === itemKey) {
                // Clone the item object for later use
                item = { ...oItem };

                // Calculate total discounted quantity, including loyalty point coupons
                discountedQuantity += oItem.DiscountedQuantity; // Refactor + globalThis.aOLO.Temp.loyaltyPointCoupons.filter(x => x.itemKey === iKey).length;

                // Redeem points if the item is redeemed and there are loyalty point coupons
                if (oItem.IsRedeemed)// Refactor && globalThis.aOLO.Temp.loyaltyPointCoupons.length > 0)
                    await OnlineOrderingUtil.redeemPointsAsync(null, oItem.ItemKey);

                // Remove the item directly from the order's items array
                items.splice(i, 1);
            }
        }

        if (this._dataLayer && item)
            this._dataLayer.remove_from_cart(this, item);

        if (discountedQuantity > 0 || this._order.ItemsCoupons.some(ic => ic.ItemKey == itemKey)) {
            const removedCoupons = await this.removeCouponByItemKeyAsync(loyaltyProvider, itemKey);
            couponsToApplyAfterTotals = await this._reviewRemovedCouponsForOrderAsync(loyaltyProvider, removedCoupons);
        }

        //this.TaxOrder();

        //Reapply Percent Off entire order coupon after calculating totals
        for (const reapplyCoupon of couponsToApplyAfterTotals)
            await this._reapplyOrMarkIncompleteAsync(loyaltyProvider, reapplyCoupon, this._hasSpecialOfferCode(reapplyCoupon));

        this.calculateTotals();
        await this._checkItemsCouponsMeetMinimumOrderAmountAsync(loyaltyProvider);
        this._saveCartToLocalStorage([itemKey], undefined, false, true);
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    private readonly _checkItemsCouponsMeetMinimumOrderAmountAsync = async (loyaltyProvider: ILoyaltyProvider): Promise<void> => {
        let removedCoupons: IRemovedCoupon[] = [];
        for (const oCoupon of this._order.Coupons) {
            const coupon = this.couponGetCoupon(oCoupon.CouponId, true, this._order.OrderTypeID, oCoupon.ChannelID);
            if (coupon) {
                const isMinimumOrderAmountOkay = this.checkMinimumOrderAmount(coupon, true, true);

                if (!isMinimumOrderAmountOkay) {
                    //remove coupon by item coupon key
                    const iCoupon = this._order.ItemsCoupons.find(ic => ic.CouponKey == oCoupon.CouponKey);
                    if (iCoupon)
                        removedCoupons = await this.removeCouponByItemKeyAsync(loyaltyProvider, iCoupon.ItemKey, true, true);
                }
            }
        }

        if (removedCoupons.length > 0) {
            await this._reviewRemovedCouponsForOrderAsync(loyaltyProvider, removedCoupons);
        }
    }

    // Plain codes now carry offerCode too, so only special code types fall back as campaign codes
    private readonly _hasSpecialOfferCode = (removedCoupon: IRemovedCoupon): boolean =>
        !!removedCoupon.offerCode &&
        (removedCoupon.isCampaignCode || removedCoupon.isPromotionCode || removedCoupon.sourceId === 1 || removedCoupon.isSpecialCode || !!removedCoupon.isSingleUseCoupon);

    private readonly _reviewRemovedCouponsForOrderAsync = async (loyaltyProvider: ILoyaltyProvider, removedCoupons: IRemovedCoupon[]): Promise<IRemovedCoupon[]> => {
        const couponsToApplyAfterTotals: IRemovedCoupon[] = []
        for (const removedCoupon of removedCoupons) {
            if (removedCoupon.couponId === CouponIdList.LOYALTY_REDEEM_ITEM)
                continue;

            const coupon = this.couponGetCoupon(removedCoupon.couponId, true, this._order.OrderTypeID, null);
            if (coupon?.Items.length === 0 && coupon.CouponTypeId === CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER) {
                couponsToApplyAfterTotals.push(removedCoupon);
                continue;
            }

            await this._reapplyOrMarkIncompleteAsync(loyaltyProvider, removedCoupon, this._hasSpecialOfferCode(removedCoupon) && removedCoupon.sourceId !== 1);
        }
        return couponsToApplyAfterTotals;
    }

    private readonly _reapplyOrMarkIncompleteAsync = async (loyaltyProvider: ILoyaltyProvider, removedCoupon: IRemovedCoupon, isCampaignCode: boolean): Promise<void> => {
        const params: IApplyCouponByIdParamsPartial = {
            loyaltyProvider: loyaltyProvider,
            couponId: removedCoupon.couponId,
            sourceId: removedCoupon.sourceId,
            isCampaignCode: removedCoupon.isCampaignCode,
            couponCode: removedCoupon.offerCode,
            isPromotionCode: removedCoupon.isPromotionCode,
            isBankedCurrency: removedCoupon.isBankedCurrency,
            channelId: removedCoupon.channelId,
            rewardId: removedCoupon.rewardId,
            isSUCValidationReq: Coupon.SucValidationNeeded(removedCoupon.isPromotionCode, removedCoupon.offerCode),
            isSpecialCode: removedCoupon.isSpecialCode || false,
            isSingleUseCoupon: removedCoupon.isSingleUseCoupon
        };

        const result = await this.applyCouponByIdAsync(params);
        if (result)
            return;

        const incompleteDiscount: IIncompleteDiscount = {
            couponId: removedCoupon.couponId,
            couponCode: removedCoupon.offerCode || '',
            channelId: null,
            isCampaignCode: isCampaignCode,
            isPromotionCode: false,
            rewardId: removedCoupon.rewardId,
            isBankedCurrency: removedCoupon.isBankedCurrency,
            sourceId: removedCoupon.sourceId,
            isSingleUseCoupon: removedCoupon.isSingleUseCoupon
        };
        this.addIncompleteDiscount(loyaltyProvider, incompleteDiscount);
    }

    public changeQuantityByItemKeyAsync = async (loyaltyProvider: ILoyaltyProvider, itemKey: number, quantity: number): Promise<void> => {
        const oItems = this.getOrderItemsByItemKey(itemKey);
        if (oItems.length == 0)
            return;

        for (const oItem of oItems) {
            oItem.Quantity = quantity;

            // If item is already redeemed, unredeem item, as process changes
            if (oItem.IsRedeemed) {
                const itemCoupon = this._order.ItemsCoupons.find(x => x.ItemKey === itemKey) || null;
                if (itemCoupon)
                    await this.removeCouponByKeyAsync(loyaltyProvider, itemCoupon.CouponKey, true, false);
            }

            const mItem = this._data.getProperty("Items").find(x => x.ItemId === oItem.ItemId) || null;
            OnlineOrderingUtil.TaxItem(mItem, oItem, this.getItemTaxDependencies());
        }

        // BUG: 68816 - remove all Percentage Off Entire Order coupons to re-apply after to allow the item's coupons to re-apply to items.
        // Percentage Off Entire Order coupon type will mark all as discounted when allowed to be applied first
        const couponParamsToReapply: IApplyCouponByIdParams[] = await this._processCouponsToApplyLastAsync(loyaltyProvider);

        const removedCouponIds = await this.removeCouponByItemKeyAsync(loyaltyProvider, itemKey, false, false);
        for (const removedCoupon of removedCouponIds) {
            const params: IApplyCouponByIdParamsPartial = {
                loyaltyProvider: loyaltyProvider,
                couponId: removedCoupon.couponId,
                sourceId: removedCoupon.sourceId,
                isCampaignCode: removedCoupon.isCampaignCode,
                couponCode: removedCoupon.offerCode,
                isPromotionCode: removedCoupon.isPromotionCode,
                isBankedCurrency: removedCoupon.isBankedCurrency,
                rewardId: removedCoupon.rewardId,
                channelId: removedCoupon.channelId,
                isSUCValidationReq: Coupon.SucValidationNeeded(removedCoupon.isPromotionCode, removedCoupon.offerCode),
                isSpecialCode: removedCoupon.isSpecialCode || false,
                isSingleUseCoupon: removedCoupon.isSingleUseCoupon
            };
            await this.applyCouponByIdAsync(params);
        }

        this.calculateTotals();
        await this.autoApplyCouponsAsync(loyaltyProvider, removedCouponIds.map(c => c.couponId));

        for (const couponParams of couponParamsToReapply) {
            await this.applyCouponByIdAsync(couponParams);
        }

        this._saveCartToLocalStorage([itemKey], quantity, false, false);
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    /**
     * Adds a modifier to every half of a cart item and re-prices it (cart upsell carousel).
     * The item's coupons are removed and re-applied, as when the item is edited in Customize.
     * @param replacesSelection - Single-choice group: the modifier replaces the group's current selection.
     * @returns False if the item isn't in the cart or can't be priced; the cart is then unchanged.
     */
    public addModifierByItemKeyAsync = async (loyaltyProvider: ILoyaltyProvider, itemKey: number, modifier: IOrderItemModifier,
        replacesSelection: boolean = false): Promise<boolean> => {
        const oItems = this.getOrderItemsByItemKey(itemKey);
        if (oItems.length === 0)
            return false;

        const removedCoupons = await this.removeCouponByItemKeyAsync(loyaltyProvider, itemKey);

        const items: IOrderItem[] = oItems.map(oItem => ({
            ...oItem,
            Modifiers: OnlineOrderingUtil.WithUpsellModifier(oItem.Modifiers, modifier, replacesSelection),
            BeforeTaxDiscount: 0,
            AfterTaxDiscount: 0,
            DiscountedMarkedQuantity: 0,
            DiscountedQuantity: 0
        }));
        const added = await this.addItems(loyaltyProvider, items);

        // A coupon the new modifier disqualifies stays in the cart as an incomplete discount.
        for (const removedCoupon of removedCoupons)
            await this._reapplyOrMarkIncompleteAsync(loyaltyProvider, removedCoupon, this._hasSpecialOfferCode(removedCoupon) && removedCoupon.sourceId !== 1);

        this.calculateTotals();
        await this.autoApplyCouponsAsync(loyaltyProvider, removedCoupons.map(c => c.couponId));
        this._eventBus.emit(EventTypes.CART_CHANGED);
        return added;
    }

    public getAppliedCouponsByType(couponTypesList: CouponTypeList[]): Map<number, { orderCoupon: IOrderCoupon; dataCoupon: IDataCoupon }> {
        const eligibleCouponTypes = new Set<number>(couponTypesList);
        const couponCache = new Map<string, IDataCoupon | null>();

        return this._order.Coupons.reduce<Map<number, { orderCoupon: IOrderCoupon; dataCoupon: IDataCoupon }>>(
            (map, coupon) => {
            if (coupon.RewardId) return map;

            const cacheKey = `${coupon.CouponId}|${this._order.OrderTypeID}|${coupon.ChannelID}`;

            let mCoupon = couponCache.get(cacheKey);
            if (mCoupon === undefined) {
                mCoupon =
                this.couponGetCoupon(
                    coupon.CouponId,
                    true,
                    this._order.OrderTypeID,
                    coupon.ChannelID
                ) ?? null;

                couponCache.set(cacheKey, mCoupon);
            }

            if (mCoupon && eligibleCouponTypes.has(mCoupon.CouponTypeId)) {
                map.set(coupon.CouponKey, { orderCoupon: coupon, dataCoupon: mCoupon });
            }

            return map;
        },
        new Map<number, { orderCoupon: IOrderCoupon; dataCoupon: IDataCoupon }>()
      );
    }

    private async _processCouponsToApplyLastAsync(loyaltyProvider: ILoyaltyProvider): Promise<IApplyCouponByIdParams[]> {
        // BUG: 68816 - remove all Percentage Off Entire Order coupons to re-apply after to allow the item's coupons to re-apply to items.
        // Percentage Off Entire Order coupon type will mark all as discounted when allowed to be applied first
        const couponParamsToReapply: IApplyCouponByIdParams[] = []

        const appliedPercentOffOrderCoupons = this.getAppliedCouponsByType([CouponTypeList.PERCENTAGE_OFF_ENTIRE_ORDER]);

        for (const [key, coupon] of appliedPercentOffOrderCoupons) {
            const orderCoupon = coupon.orderCoupon;
            await this.removeCouponByKeyAsync(loyaltyProvider, key, false, false);
            const params: IApplyCouponByIdParams = {
                order: this._order,
                incompleteDiscounts: this._incompleteDiscounts,
                loyaltyProvider: loyaltyProvider,
                couponId: orderCoupon.CouponId,
                couponCode: orderCoupon.CouponCode,
                channelId: orderCoupon.ChannelID,
                isCampaignCode: orderCoupon.IsCampaignCode,
                isPromotionCode: orderCoupon.IsPromotionCode,
                rewardId: orderCoupon.RewardId,
                isBankedCurrency: orderCoupon.IsBankedCurrency,
                sourceId: orderCoupon.SourceId,
                isSUCValidationReq: Coupon.SucValidationNeeded(orderCoupon.IsPromotionCode, orderCoupon.CouponCode),
                isSpecialCode: orderCoupon.IsSpecialCode ?? false,
                isSingleUseCoupon: orderCoupon.IsSingleUseCoupon
            };
            couponParamsToReapply.push(params);
        }
        return couponParamsToReapply;
    }

    public getOrderItemsByItemKey = (itemKey: number): IOrderItem[] => {
        return this._order.Items.filter(oItem => oItem.ItemKey === itemKey);
    }

    // Re-prices every cart item (e.g. after a store switch); one that can no longer be
    // priced is removed with a notice rather than kept at $0.
    private readonly _recalculateOrderPricesAsync = async (loyaltyProvider: ILoyaltyProvider): Promise<void> => {
        // The store's menu isn't loaded yet; DATA_RETRIEVED re-prices once it arrives
        if (this._data.getProperty("Items").length === 0)
            return;

        const unpriceableRecIds = this._priceItemsAndCollectUnpriceable();
        if (unpriceableRecIds.size > 0)
            this._removeUnpriceableItems(unpriceableRecIds);

        await this.removeAndReapplyAllCouponsAsync(loyaltyProvider);
    }

    private readonly _priceItemsAndCollectUnpriceable = (): Set<number> => {
        const unpriceableRecIds = new Set<number>();
        for (const oItem of this._order.Items) {
            if (this._isPricedLine(oItem) && !this._tryPriceItem(oItem))
                this._addUnpriceableRecIds(oItem, unpriceableRecIds);
        }
        return unpriceableRecIds;
    }

    // A half & half is priced through its first half, which also prices the other half
    private readonly _isPricedLine = (oItem: IOrderItem): boolean =>
        oItem.HalfCount === 1 || (oItem.HalfCount === 2 && oItem.HalfIndex === 1);

    private readonly _tryPriceItem = (oItem: IOrderItem): boolean => {
        try {
            OnlineOrderingUtil.PriceItem(this._data, this._order.Items, oItem, this.getItemTaxDependencies(), this.getProperty("OrderTypeSubType").PriceGroupId);
            return true;
        } catch (e) {
            if (!(e instanceof ItemPricingUnavailableError))
                throw e;
            return false;
        }
    }

    private readonly _addUnpriceableRecIds = (oItem: IOrderItem, unpriceableRecIds: Set<number>): void => {
        unpriceableRecIds.add(oItem.ItemRecId);
        if (oItem.HalfCount !== 2)
            return;

        const otherHalf = OnlineOrderingUtil.GetOtherHalfItem(oItem.ItemKey, Math.round(((oItem.HalfIndex % 2) + 1)), this._order.Items);
        if (otherHalf)
            unpriceableRecIds.add(otherHalf.ItemRecId);
    }

    private readonly _removeUnpriceableItems = (unpriceableRecIds: Set<number>): void => {
        const isAllItems = unpriceableRecIds.size === this._order.Items.length;
        for (const recId of unpriceableRecIds)
            this.removeItemByItemRecId(recId);

        const name = isAllItems ? "CART_ITEMS_REMOVED_NOTICE" : "CART_UPDATED_NOTICE";
        new TimedMessage(this._localizationService.names(name), undefined, 1500).show();
    }

    public removeAndReapplyAllCouponsAsync = async (loyaltyProvider: ILoyaltyProvider, fromFutureDateChange: boolean = false): Promise<void> => {
        //Bug #73727 - Prevent multiple simultaneous remove and re-apply processes coming from order type and future date changes
        let emitCartChange = false;
        if (this._context.getProperty("couponRemoveReapplyInProgress")) {
            return;
        }

        this._context.setProperty("couponRemoveReapplyInProgress", true)
        const removedCouponIds = await this._coupon.RemoveCouponAll(this._order, this._incompleteDiscounts, loyaltyProvider);
        for (const removedCoupon of removedCouponIds) {
            // Bug #86819 - RemoveCouponAll already re-ran auto-apply; a second apply fails and shows "Coupon Not Qualified"
            if (removedCoupon.autoApplied)
                continue;

            const params: IApplyCouponByIdParams = {
                order: this._order,
                loyaltyProvider: loyaltyProvider,
                incompleteDiscounts: this._incompleteDiscounts,
                showErrorMsg: fromFutureDateChange,
                couponId: removedCoupon.couponId,
                couponCode: removedCoupon.offerCode,
                channelId: removedCoupon.channelId,
                isCampaignCode: removedCoupon.isCampaignCode,
                isPromotionCode: removedCoupon.isPromotionCode,
                rewardId: removedCoupon.rewardId,
                isBankedCurrency: removedCoupon.isBankedCurrency,
                sourceId: removedCoupon.sourceId,
                isSpecialCode: removedCoupon.isSpecialCode || false,
                isSUCValidationReq: fromFutureDateChange || (Coupon.SucValidationNeeded(removedCoupon.isPromotionCode, removedCoupon.offerCode)),
                isSingleUseCoupon: removedCoupon.isSingleUseCoupon,
            };
            const result = await this.applyCouponByIdAsync(params);
            if (!result && fromFutureDateChange)
                emitCartChange = true;
        }

        this._context.setProperty("couponRemoveReapplyInProgress", false);
        if (emitCartChange)
            this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public readonly getItemTaxDependencies = (): IGetItemTaxDependencies => {
        const dependencies: IGetItemTaxDependencies = {
            taxesData: this._data.getProperty("Taxes"),
            zipTaxesData: this._data.getProperty("ZipTaxes"),
            isCallCenter: this._data.getProperty("Settings").ISCC,
            orderTypeId: this._order.OrderTypeID,
            address: this._order.Address
        };
        return dependencies;
    }

    public readonly setOrderTypeChargeAsync = async (): Promise<void> => {
        let zoneCharge = 0.0;
        if (this._order.OrderTypeSubType.OrderTypeCharge > 0 && this._order.Address) {
            const zoneId = this._order.Address.ZoneID;
            if (zoneId > 0) {
                const zone = this._getZone(zoneId, this._data.getProperty("DeliveryZones"));
                if (zone)
                    zoneCharge = Util.Float2(zone.ZoneCharge);
            }

            await this.setOrderDeliveryDistanceFromStoreAsync(zoneCharge);
        } else
            this.setOrderDeliveryCharge(zoneCharge);
    }

    private _getZone(zoneId: number, deliveryZones: IDataDeliveryZone[]): IDataDeliveryZone | null {
        return deliveryZones.find(zone => zone.ZoneId == zoneId) || null;
    }

    public readonly setOrderDeliveryDistanceFromStoreAsync = async (zoneCharge: number): Promise<void> => {
        try {
            if (!this._order.Address) {
                this._order.Distance = 0;
                return;
            }

            const key = getGeoServiceApiKeys(this._data, this._context).google;
            const loader = GoogleMapsLoader.getLoader(key);
            await loader.importLibrary("maps");

            const storeInfo = this._data.getProperty("StoreInfo");
            const storeLocation: google.maps.LatLng = new google.maps.LatLng(Util.Float12(storeInfo.latitude), Util.Float12(storeInfo.longitude));
            const deliveryLocation: google.maps.LatLng = new google.maps.LatLng(Util.Float12(this._order.Address.Latitude), Util.Float12(this._order.Address.Longitude));

            const request: google.maps.DirectionsRequest = {
                origin: storeLocation,
                destination: deliveryLocation,
                //@ts-ignore
                travelMode: google.maps.TravelMode.DRIVING,
                //@ts-ignore
                unitSystem: google.maps.UnitSystem.IMPERIAL
            };

            const distance = await this._getRouteDistance(request);
            this._order.Distance = distance;
            this.setOrderDeliveryCharge(zoneCharge);
        } catch (ex: unknown) {
            if (ex instanceof Error)
                // Util.LogError("GetDeliveryDistanceFromStore", ex, globalThis.aOLO);

                this._order.Distance = 0;
        }
    }

    private async _getRouteDistance(request: google.maps.DirectionsRequest): Promise<number> {
        const service = new google.maps.DirectionsService();

        const response = await new Promise<google.maps.DirectionsResult>((resolve, reject) => {
            service.route(request, (result, status) => {
                if (status === google.maps.DirectionsStatus.OK && result) {
                    resolve(result);
                } else {
                    reject(new Error("Directions request failed: " + status));
                }
            });
        });

        const meters = response.routes[0].legs[0].distance?.value ?? 0;
        const miles = Util.Float2(meters / 1609.34);
        return miles > 999 ? 0 : miles;
    }

    private readonly selectStoreForCheckedAddress = async (): Promise<void> => {
        // 2DO: Refactor When Call Center Functionality Added
        //if (!this._order.Address)
        //    return;

        //const zone = OnlineOrderingUtil.CheckZones(this._order.Address.Latitude, this._order.Address.Longitude, []); //aOLO.data.DeliveryZones
        //if (zone?.ZTID == 1)
        //    await this.setStoreOrderInfo(zone.SID, this._order.OrderTypeID);
    }

    private readonly setStoreOrderInfo = async (storeId: number, orderTypeId: number): Promise<void> => {
        // 2DO: Refactor When Call Center Functionality Added
        //const store = OnlineOrderingUtil.getCallCenterStore(storeId, this._data.getProperty("Stores"));
        //if (!store) {
        //    if (aOLO.Dialog.CallCenterStores)
        //        aOLO.Dialog.CallCenterStores.closeDialog();
        //    return;
        //}

        //this._order.StoreID = storeId;

        ////aOLO.Modules.Categories = new Categories(aOLO.data.Categories, aOLO);
        ////aOLO.Modules.OrderCart = new OrderCart(getOrderCartFunctions());

        ////GUI_LoadItems();

        //await setOrderType(orderTypeId);

        //aOLO.Temp.WaitTime = OnlineOrderingUtil.getStoreWaitTime(store, orderTypeId, aOLO.Temp.TimeOffset);

        //if (orderTypeId !== OrderType.DELIVERY)
        //    delete aOLO.Temp.Address;

        //if (aOLO.Dialog.OrderType) {
        //    aOLO.Dialog.OrderType.handleOrderTypeOrderTimeChange(false, false, true);
        //    aOLO.Dialog.OrderType.setStoreWaitTimes(store);
        //    aOLO.Dialog.OrderType.setOrderTypeTime();
        //    aOLO.Dialog.OrderType.setStoreInfo();
        //}

        //GUI_UpdateHeaderStoreAddress();
    }

    public setOrderDeliveryCharge = (zoneCharge: number): void => {
        this._order.OrderTypeCharge = 0;
        let orderTypeCharge = 0;

        if (this._data.getProperty("Settings").CRGMIL > 0 && this._data.getProperty("Settings").MDCRGMIL > 0 && this._order.Distance > 0 && this._order.OrderTypeSubType.OrderTypeCharge > 0) {
            const orderTypeCharge = Util.Float2(Math.max(this._order.Distance * this._data.getProperty("Settings").CRGMIL, this._data.getProperty("Settings").MDCRGMIL));
            this._order.OrderTypeCharge = orderTypeCharge;
        } else if (this._order.OrderTypeSubType.OrderTypeCharge && this._order.OrderTypeSubType.OrderTypeCharge > 0) {
            orderTypeCharge = Util.Float2(this._order.OrderTypeSubType.OrderTypeCharge) + zoneCharge;
            if (orderTypeCharge > 0)
                this._order.OrderTypeCharge = orderTypeCharge;
        } else {
            orderTypeCharge = zoneCharge;
            if (orderTypeCharge > 0)
                this._order.OrderTypeCharge = orderTypeCharge;
        }

        this.calculateTotals();
    }

    public couponGetCoupon = (couponId: number, checkOrderTypeSubType: boolean, orderTypeId: number | null, channelId?: number | null): IDataCoupon | null => {
        if (this._couponService.isStoreSupported())
            return this._couponService.getCoupon(couponId, checkOrderTypeSubType, orderTypeId, channelId || null);
        else
            return this._coupon.GetCoupon(couponId, checkOrderTypeSubType, orderTypeId, channelId || null);
    }

    public removeCouponByKeyAsync = async (loyaltyProvider: ILoyaltyProvider, couponKey: number, refresh: boolean, autoApply: boolean): Promise<void> => {
        if (this._couponService.isStoreSupported())
            await this._couponService.removeCouponByKeyAsync(this._incompleteDiscounts, loyaltyProvider, couponKey, refresh);
        else
            await this._coupon.RemoveCouponByKey(this._order, this._incompleteDiscounts, loyaltyProvider, couponKey, refresh, autoApply);
        this.calculateTotals();
    }

    public removeCouponByItemKeyAsync = async (loyaltyProvider: ILoyaltyProvider, itemKey: number | null, refresh: boolean = false, autoApply: boolean = false): Promise<IRemovedCoupon[]> => {
        let removed;
        if (this._couponService.isStoreSupported())
            removed = await this._couponService.removeCouponByItemKeyAsync(this._incompleteDiscounts, loyaltyProvider, itemKey, refresh);
        else
            removed = await this._coupon.RemoveCouponByItemKey(this._order, this._incompleteDiscounts, loyaltyProvider, itemKey, refresh, autoApply);
        this.calculateTotals();
        return removed;
    }

    public removeIncompleteCouponByIdAsync = async (loyaltyProvider: ILoyaltyProvider, couponId: number, appliedToOrder: boolean = false): Promise<void> => {
        if (this._couponService.isStoreSupported())
            await this._couponService.removeIncompleteCouponByIdAsync(this._incompleteDiscounts, loyaltyProvider, couponId);
        else
            await this._coupon.RemoveIncompleteCouponById(this._order, this._incompleteDiscounts, loyaltyProvider, couponId, appliedToOrder);
    }

    public isCouponAllowedInFundraiser = (showError: boolean = true): boolean => {
        if (this._couponService.isStoreSupported())
            return this._couponService.isCouponAllowedInFundraiser(showError);
        else
            return this._coupon.isCouponAllowedInFundraiser(this._order, showError);
    }

    public isCouponAllowedInChallengeEvent(showError: boolean = true): boolean {
        if (this._couponService.isStoreSupported())
            return this._couponService.isCouponAllowedInChallengeEvent(showError);
        else
            return this._coupon.isCouponAllowedInChallengeEvent(this._order, showError);
    }

    public checkCouponIdScheduleForOffer = async (couponId: number): Promise<boolean> => {
        //check if coupon exists in data first and attempt to pull
        let coupon = this.couponGetCoupon(couponId, false, null, null);
        if (!coupon) {
            await this._coupon.loadCouponByIdAsync(couponId);
        }

        //then enforce order type availability
        coupon = this.getCoupon(couponId, true, this._order.OrderTypeID, null);
        if (!coupon) {
            return false;
        }

        return this._coupon.CheckCouponSCHS(this._order, coupon);
    }

    public checkCouponHasQualifyingItemsAsync = async (loyaltyProvider: ILoyaltyProvider, params: ICheckCouponHasQualifyingItemsParams): Promise<void> => {
        const newParams: ICouponState = {
            couponId: params.couponId,
            couponCode: params.couponCode ?? undefined,
            rewardId: params.reward?.RewardId ?? undefined,
            isCampaignCode: params.isCampaignCode,
            isPromotionCode: params.isPromotionCode,
            sourceId: params.sourceId ?? undefined,
            orderId: params.orderId ?? undefined,
            isSpecialCode: params.isSpecialCode ?? undefined,
            isSingleUseCoupon: params.isSingleUseCoupon
        };
        if (this._couponService.isStoreSupported() && (await this._couponService.isCouponSupportedAsync(newParams))) {
            if (this._context.getProperty("developmentMode"))
                console.log(this._newCouponCodeMsg);

            await this._couponService.checkCouponHasQualifyingItemsAsync(
                this._incompleteDiscounts,
                loyaltyProvider,
                newParams,
                { showErrorMsg: params.showErrorMsg ?? false, showApplyMsg: params.showApplyMsg ?? true, addIncomplete: true }
            );
        }
        else {
            if (this._context.getProperty("developmentMode"))
                console.log(this._legacyCouponCodeMsg);

            const incompleteDiscount = await this._coupon.CheckCouponHasQualifyingItems(this._eventBus, this._order, this._incompleteDiscounts, loyaltyProvider, params);
            this._addIncompleteDiscountFromCoupon(loyaltyProvider, incompleteDiscount);
        }
    }

    public getCouponQualifyingItems = (coupon: IDataCoupon): IvItem[] => {
        return this._coupon.QualifyCouponItems(this._order, coupon, false, true, true, true);
    }

    public autoApplyCouponsAsync = async (loyaltyProvider: ILoyaltyProvider, couponIdsToSkip: number[] = []): Promise<void> => {
        if (this._couponService.isStoreSupported() && (await this._couponService.areAllCouponsSupportedAsync(this._incompleteDiscounts))) {
            if (this._context.getProperty("developmentMode"))
                console.log(this._newCouponCodeMsg);

            await this._couponService.autoApplyCouponsAsync(this._incompleteDiscounts, loyaltyProvider);
        }
        else {
            if (this._context.getProperty("developmentMode"))
                console.log(this._legacyCouponCodeMsg);

            await this._coupon.AutoApplyCoupons(this._order, this._incompleteDiscounts, loyaltyProvider, couponIdsToSkip);
        }
    }

    public applyCouponByCodeAsync = async (loyaltyProvider: ILoyaltyProvider, couponCode: string, isOffer: boolean = false, isPromotion: boolean = false, isCampaign: boolean = false, isSpecialCode: boolean = false): Promise<boolean> => {
        // Wallet coupons are identified by membership in the user's wallet (fetched from Customer.Api),
        // not by the code's shape/prefix, so they're applied by couponId rather than a code lookup.
        const walletCoupon = this._user.getSingleUseCouponInfo(couponCode);
        if (walletCoupon) {
            // Rewards page reuses this entry point for both Redeem and Void clicks -- if the coupon
            // is already applied or still pending in a meal deal, this click is a Void.
            const appliedCoupon = this._order.Coupons?.find(x => x.CouponCode.toLowerCase() === couponCode.toLowerCase()) || null;
            if (appliedCoupon) {
                await this.removeCouponByKeyAsync(loyaltyProvider, appliedCoupon.CouponKey, true, true);
                return false;
            }

            const incompleteCoupon = this.getIncompleteDiscounts().find(x => (x.couponCode ?? '').toLowerCase() === couponCode.toLowerCase());
            if (incompleteCoupon) {
                await this.removeIncompleteCouponByIdAsync(loyaltyProvider, incompleteCoupon.couponId);
                return false;
            }

            // Routes through the qualifying-items check so a meal-deal coupon opens the Deal Builder
            // instead of failing apply with a misleading minimum-order-amount error.
            const params: ICheckCouponHasQualifyingItemsParams = {
                couponId: walletCoupon.couponId,
                couponCode: walletCoupon.couponCode,
                isSingleUseCoupon: true,
                showErrorMsg: true,
                showApplyMsg: true
            };
            await this.checkCouponHasQualifyingItemsAsync(loyaltyProvider, params);

            // A coupon pending in a meal deal counts as redeemed too -- no need to wait for it to be built.
            const isApplied = this._order.Coupons?.some(x => x.CouponCode.toLowerCase() === couponCode.toLowerCase()) ?? false;
            const isIncomplete = this.getIncompleteDiscounts().some(x => (x.couponCode ?? '').toLowerCase() === couponCode.toLowerCase());
            return isApplied || isIncomplete;
        }

        if (this._couponService.isStoreSupported() && (await this._couponService.isCouponSupportedAsync({ couponCode: couponCode }))) {
            if (this._context.getProperty("developmentMode"))
                console.log(this._newCouponCodeMsg);

            const result = await this._couponService.applyCouponByCodeAsync(this._incompleteDiscounts, loyaltyProvider, couponCode); // new coupon service handles adding incomplete discount
            return result.success;
        }
        else {
            if (this._context.getProperty("developmentMode"))
                console.log(this._legacyCouponCodeMsg);

            const result = await this._coupon.ApplyCouponByCode(this._order, this._incompleteDiscounts, loyaltyProvider, couponCode, isOffer, isPromotion, isCampaign, isSpecialCode);
            if (result.incompleteDiscount)
                this._addIncompleteDiscountFromCoupon(loyaltyProvider, result.incompleteDiscount);

            return result.success;
        }
    }

    private _addIncompleteDiscountFromCoupon(loyaltyProvider: ILoyaltyProvider, incompleteDiscount: IIncompleteDiscount | null): void {
        if (!incompleteDiscount)
            return;

        this.addIncompleteDiscount(loyaltyProvider, incompleteDiscount);
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public applyCouponByIdAsync = async (params: IApplyCouponByIdParamsPartial): Promise<boolean | null> => {
        if (this._couponService.isStoreSupported() && (await this._couponService.isCouponSupportedAsync({ couponId: params.couponId }))) {
            if (this._context.getProperty("developmentMode"))
                console.log(this._newCouponCodeMsg);

            const newParams: ICouponState = {
                couponId: params.couponId,
                couponCode: params.couponCode ?? undefined,
                channelId: params.channelId ?? undefined,
                autoApplying: params.autoApplying,
                isCampaignCode: params.isCampaignCode,
                isPromotionCode: params.isPromotionCode,
                sourceId: params.sourceId ?? undefined,
                runBatch: params.runBatch,
                isSingleUseCoupon: params.isSingleUseCoupon,
            };
            const result = await this._couponService.applyCouponByParamsAsync(
                this._incompleteDiscounts,
                newParams,
                params.loyaltyProvider,
                { showErrorMsg: params.showErrorMsg ?? false, showApplyMsg: params.showApplyMsg ?? false, addIncomplete: true });
            return result.success;
        }
        else {
            if (this._context.getProperty("developmentMode"))
                console.log(this._legacyCouponCodeMsg);

            const finalParams: IApplyCouponByIdParams = {
                order: this._order,
                incompleteDiscounts: this._incompleteDiscounts,
                loyaltyProvider: params.loyaltyProvider,
                couponId: params.couponId,
                discount: params.discount,
                showErrorMsg: params.showErrorMsg,
                showApplyMsg: params.showApplyMsg,
                couponCode: params.couponCode,
                itemKey: params.itemKey,
                channelId: params.channelId ?? null,
                autoApplying: params.autoApplying,
                isCampaignCode: params.isCampaignCode ?? false,
                isPromotionCode: params.isPromotionCode ?? false,
                rewardId: params.rewardId ?? null,
                runBatch: params.runBatch,
                isBankedCurrency: params.isBankedCurrency ?? false,
                sourceId: params.sourceId ?? null,
                isSUCValidationReq: params.isSUCValidationReq,
                isSpecialCode: params.isSpecialCode ?? false,
                isSingleUseCoupon: params.isSingleUseCoupon
            };
            return await this._coupon.ApplyCouponByID(finalParams, this._eventBus);
        }
    }

    public checkMinimumOrderAmount = (coupon: IDataCoupon, autoApplying: boolean, isVerifying: boolean): boolean => {
        return this._coupon.checkMinimumOrderAmount(this._order, coupon, autoApplying, isVerifying, true);
    }

    public checkCouponOrderTypeSubType = (coupon: IDataCoupon, orderTypeId: number): boolean => {
        return this._coupon.CheckCouponOTST(coupon, orderTypeId);
    }

    public checkCouponSchedule = (coupon: IDataCoupon): boolean => {
        return this._coupon.CheckCouponSCHS(this._order, coupon);
    }

    public verifyCouponDate = (coupon: IDataCoupon): boolean => {
        return this._coupon.VerifyCouponDate(this._order, coupon);
    }

    public getCoupon = (couponId: number, checkOrderTypeSubType: boolean, orderTypeId: number | null, channelId: number | null): IDataCoupon | null => {
        return this._coupon.GetCoupon(couponId, checkOrderTypeSubType, orderTypeId, channelId);
    }

    public getCouponItemSizesByGroup(coupon: IDataCoupon | null, itemId: number, groupIndex: number): number[] {
        return this._coupon.getCouponItemSizesByGroup(coupon, itemId, groupIndex);
    }

    public processRewardAsync = async (reward: IUserLoyaltyReward, loyaltyProvider: ILoyaltyProvider): Promise<boolean> => {
        let isUsed = false;
        if (!reward.Used) {
            isUsed = true;
            await loyaltyProvider.addReward(reward);
        } else {
            const refreshCart = await loyaltyProvider.removeReward(reward);
            if (refreshCart)
                this._eventBus.emit(EventTypes.CART_CHANGED);
        }

        return isUsed;
    }

    public processOfferAsync = async (offer: IUserCampaignCodesData, loyaltyProvider: ILoyaltyProvider): Promise<boolean> => {
        if (offer.Used) {
            await this._voidOfferAsync(offer.CouponCode, loyaltyProvider);
            return false;
        }

        return await this._applyOfferAsync(offer, loyaltyProvider);
    }

    private async _applyOfferAsync(offer: IUserCampaignCodesData, loyaltyProvider: ILoyaltyProvider): Promise<boolean> {
        const isCampaignCode = (offer.IsSingleUseCoupon && !offer.IsCouponPromotionCode);
        const isPromotionCode = (offer.IsSingleUseCoupon && offer.IsCouponPromotionCode);
        return await this.applyCouponByCodeAsync(loyaltyProvider, offer.CouponCode, true, isPromotionCode, isCampaignCode);
    }

    private async _voidOfferAsync(couponCode: string, loyaltyProvider: ILoyaltyProvider): Promise<void> {
        const usedOffer = this._user.getProperty("Offers")?.Codes.find((x: IUserCampaignCodesData) => x.CouponCode == couponCode && x.Used) || null;
        if (!usedOffer)
            return;
        usedOffer.Used = false;

        const coupon = this._order.Coupons?.find(x => x.CouponCode.toLowerCase() === couponCode.toLowerCase()) || null;
        if (coupon) {
            await this.removeCouponByKeyAsync(loyaltyProvider, coupon.CouponKey, true, true);
        } else if (this.getIncompleteDiscounts().length > 0) {
            const incompleteCoupon = this.getIncompleteDiscounts().find((x: IIncompleteDiscount) => x.couponCode === couponCode.toLowerCase());
            if (incompleteCoupon) {
                await this.removeIncompleteCouponByIdAsync(loyaltyProvider, incompleteCoupon.couponId);
            }
        }
    }

    public async processCreditAsync(loyaltyProvider: ILoyaltyProvider, credit: IUserNextOrderCredit): Promise<boolean> {
        let isUsed = false;
        const creditCoupon = this.couponGetCoupon(credit.CouponID, true, this._order.OrderTypeID, null);
        if (!creditCoupon)
            return isUsed;

        if (!credit.Used) {
            await this._applyCreditAsync(loyaltyProvider, credit, creditCoupon);
            isUsed = true;
        } else {
            await this._voidCreditAsync(loyaltyProvider, credit, creditCoupon);
        }

        return isUsed;
    }

    private async _applyCreditAsync(loyaltyProvider: ILoyaltyProvider, credit: IUserNextOrderCredit, creditCoupon: IDataCoupon): Promise<void> {
        const params: ICheckCouponHasQualifyingItemsParams = {
            couponId: creditCoupon.CouponId,
            couponCode: credit.CustomerCreditID.toString(),
            sourceId: 1

        };
        await this.checkCouponHasQualifyingItemsAsync(loyaltyProvider, params);
    }

    private async _voidCreditAsync(loyaltyProvider: ILoyaltyProvider, credit: IUserNextOrderCredit, creditCoupon: IDataCoupon): Promise<void> {
        credit.Used = false;
        const orderCoupon = this._order.Coupons.find(x => x.CouponId === creditCoupon.CouponId);
        if (orderCoupon) {
            await this.removeCouponByKeyAsync(loyaltyProvider, orderCoupon.CouponKey, true, true)
        } else if (this.getIncompleteDiscounts().length > 0) {
            const incompleteCoupon = this.getIncompleteDiscounts().find((x: IIncompleteDiscount) => x.couponId === creditCoupon.CouponId && ((x.couponCode ?? '') === credit.CustomerCreditID.toString()));
            if (incompleteCoupon) {
                await this.removeIncompleteCouponByIdAsync(loyaltyProvider, incompleteCoupon.couponId);
            }
        }
    }

    public setFundraiser(id: number, first?: string, second?: string): void {
        this._order.Fundraiser = {
            ID: id,
            FIRST: first,
            SECOND: second
        };
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public removeFundraiser(): void {
        this._order.Fundraiser = null;
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    /**
     * Creates an order item from the provided properties
     * @returns {IOrderItem} The order item with updated properties.
     */
    public _createOrderItem(dItem: IDataItem, sizeId: number, halfIndex: number, quantity: number, oItemModifiers: IOrderItemModifier[], preTaxDiscount: number = 0, postTaxDiscount: number = 0): IOrderItem {
        const sizes = this._data.getProperty("Sizes");
        const sizeNames = JSON.stringify(sizes.find(s => s.SizeId === sizeId)?.Names || []);

        const itemPrices = this._getItemPrice(dItem, sizeId, oItemModifiers);
        const itemTaxes = this._getTaxOnItem(sizeId, quantity, itemPrices.TaxIncluded, itemPrices.Price, dItem, preTaxDiscount, postTaxDiscount);

        const oItem = this.createBaseOrderItem();
        oItem.ItemId = dItem.ItemId;
        oItem.Name = JSON.stringify(dItem.Names);
        oItem.HalfIndex = halfIndex;
        oItem.HalfCount = halfIndex == 0 ? 1 : 2;
        oItem.Index = halfIndex;
        oItem.SizeId = sizeId;
        oItem.SizeNames = sizeNames;
        oItem.Quantity = quantity;
        oItem.ItemWebCategoryId = dItem.WebCategoryId;
        oItem.ItemCategoryId = dItem.ItemCategoryId;
        oItem.IsBuildYourOwn = dItem.IsBuildYourOwn;
        oItem.Modifiers = oItemModifiers;
        oItem.PriceIncludesTax = itemPrices.TaxIncluded;
        oItem.Price = itemPrices.Price;
        oItem.MinPrice = itemPrices.MinPrice;
        oItem.HalfHalfPrice = itemPrices.Price;
        oItem.MenuPrice = itemPrices.Price;
        oItem.MenuPriceTax = itemPrices.Tax;
        oItem.Tax = itemTaxes.TotalTax;
        oItem.TaxAmount = itemTaxes.TaxableAmount;
        oItem.Taxes = itemTaxes.Taxes;
        oItem.AfterTaxDiscount = postTaxDiscount;
        oItem.BeforeTaxDiscount = preTaxDiscount;

        return oItem;
    }

    /**
     * Calculates the price of the current item based on its modifiers and pricing configurations.
     * @returns {IMenuItemPrice} - An object containing the calculated price details of the item.
     */
    public _getItemPrice(dItem: IDataItem, sizeId: number, modifiers: IOrderItemModifier[]): IMenuItemPrice {
        const orderTypePriceGroupId = this.getProperty("OrderTypeSubType").PriceGroupId;
        const defaultPriceGroup = this._data.getProperty("DefaultPriceGroupId");
        const nonChargeableDisplayGroups = this._data.getProperty("Settings").NCDG;
        const preModifiers = this._data.getProperty("PreModifiers");
        return OnlineOrderingUtil.GetItemPrice(dItem, sizeId, modifiers, orderTypePriceGroupId, defaultPriceGroup, nonChargeableDisplayGroups, preModifiers);
    }

    /**
     * gets tax details for the given item based on its properties.
     */
    private _getTaxOnItem(sizeId: number, quantity: number, priceIncludesTax: boolean, itemPrice: number, dItem: IDataItem, preTaxDiscount: number, postTaxDiscount: number): IMenuItemTax {
        const oItemTax: IGetItemTaxoItem = {
            SizeId: sizeId,
            Quantity: quantity,
            PriceIncludesTax: priceIncludesTax,
            Price: itemPrice,
            AfterTaxDiscount: preTaxDiscount,
            BeforeTaxDiscount: postTaxDiscount
        };

        const taxData = OnlineOrderingUtil.GetItemTax(dItem, oItemTax, this._getItemTaxDataDependencies());
        return taxData;
    }

    public _getItemTaxDataDependencies(): IGetItemTaxDependencies {
        return {
            taxesData: this._data.getProperty("Taxes"),
            zipTaxesData: this._data.getProperty("ZipTaxes"),
            isCallCenter: this._data.getProperty("Settings").ISCC,
            orderTypeId: this.getProperty("OrderTypeID"),
            address: this._order.Address
        };
    }

    public setChallenge(id: number, members: IOrderChallengeMember[]): void {
        this._order.Challenges = [{
            challengeId: id,
            members: members
        }];
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public removeChallenge(): void {
        this._order.Challenges = [];
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public async setSpecialCodeAsync(loyaltyProvider: ILoyaltyProvider): Promise<void> {
        const specialCode = this._context.getProperty("specialCode");
        if (specialCode && this._data.storeKey) {
            await this.applyCouponByCodeAsync(loyaltyProvider, specialCode, false, false, false, true);
            this._context.setProperty("specialCode", undefined);
            this._eventBus.emit(EventTypes.CART_CHANGED);
        }

        const cloudCoupon = this._context.getProperty("cloudCoupon");
        if (cloudCoupon) {
            await this.applyCouponByCodeAsync(loyaltyProvider, cloudCoupon.CouponCode);
            const cloudCouponInProgress = this._context.getProperty("cloudCouponInProgress");
            if (!cloudCouponInProgress)
                this._context.setProperty("cloudCoupon", undefined);
            this._eventBus.emit(EventTypes.CART_CHANGED);
        }
    }

    public async setBoxTopperAsync(loyaltyProvider: ILoyaltyProvider): Promise<void> {
        const boxTopper = this._context.getProperty("boxTopper");
        if (!boxTopper)
            return;

        if (boxTopper.StoreId !== this._order.StoreID)
            return;

        const businessDate = Util.getBusinessDate(this._data);
        if (Util.DateDiff('d', new Date(boxTopper.ExpirationDate), businessDate) > 0) {
            const params: ICheckCouponHasQualifyingItemsParams = {
                couponId: Number(boxTopper.CouponId),
                couponCode: null,
                orderId: boxTopper.OrderId
            };
            await this.checkCouponHasQualifyingItemsAsync(loyaltyProvider, params);
        }

        this._context.setProperty("boxTopper", undefined);
    }

    private _getTaxDefinitionById(taxId: number, taxes: IDataTax[]): IDataTax | undefined {
        return taxes.find(t => t.TaxId == taxId);
    }

    private _getCartItemsForLocalStorage(
        itemKeys: number[],
        quantity?: number,
        added?: boolean,
        deleted?: boolean
    ): IOrderItem[] {
        const fallback = this._order.Items;
        const dataStr = localStorage.getItem("cartData");
        if (!dataStr) return fallback;

        try {
            const parsed = JSON.parse(dataStr) as {
                expiresAt?: string;
                storeKey?: string;
                items?: IOrderItem[];
            };

            let items = parsed.items ?? [];
            if (itemKeys.length === 0)
                return items.length && added !== undefined && deleted !== undefined ? items : fallback;

            const keySet = new Set(itemKeys);

            // 1) Delete
            if (deleted) {
                items = items.filter(i => !keySet.has(i.ItemKey));
            }

            // 2) Update quantity (for remaining matches)
            if (quantity !== undefined) {
                items = items.map(i =>
                    keySet.has(i.ItemKey) ? { ...i, Quantity: quantity } : i
                );
            }

            // 3) Add/replace with current order items for those keys
            if (added) {
                const toAdd = this._order.Items.filter(i => keySet.has(i.ItemKey));
                if (toAdd.length) {
                    // Remove any existing items with those keys, then append fresh ones
                    items = items.filter(i => !keySet.has(i.ItemKey)).concat(toAdd);
                }
            }

            return items.length ? items : fallback;
        } catch (e) {
            console.warn("Error loading cart:", e);
            return fallback;
        }
    }

    private _saveCartToLocalStorage(itemKeys: number[], quantity?: number, added?: boolean, deleted?: boolean): void {
        const expirationHours = 48;
        const expiresAt = new Date();
        expiresAt.setHours(expiresAt.getHours() + expirationHours);

        const data = {
            storeKey: this._data.storeKey,
            items: this._getCartItemsForLocalStorage(itemKeys, quantity, added, deleted).map(this._withoutCouponDiscounts),
            expiresAt: expiresAt.toISOString()
        };
        localStorage.setItem("cartData", JSON.stringify(data));
    }

    // Coupons aren't saved with the cart, so drop the item discounts they produced
    private readonly _withoutCouponDiscounts = (item: IOrderItem): IOrderItem => ({
        ...item,
        IsRedeemed: false,
        BeforeTaxDiscount: 0,
        AfterTaxDiscount: 0,
        DiscountedMarkedQuantity: 0,
        DiscountedQuantity: 0,
        LoyaltyDiscountAmount: 0,
        LoyaltyDiscountsQTY: 0,
        NonLoyaltyDiscountsQTY: 0
    });

    private _updateStoreKeyInLocalStorage(storeKey: string, isCacheTrigger: boolean): void {
        if (!isCacheTrigger) return;
        const dataStr = localStorage.getItem("cartData");
        if (!dataStr)
            return;
        try {
            const data = JSON.parse(dataStr) as { expiresAt: string, storeKey: string, items: IOrderItem[] };
            data.storeKey = storeKey;
            localStorage.setItem("cartData", JSON.stringify(data));
        } catch (e) {
            console.warn("Error updating store key in cart:", e);
            localStorage.removeItem("cartData");
        }
    }

    public async loadCartFromLocalStorageAsync(loyaltyProvider: ILoyaltyProvider): Promise<void> {
        const dataStr = localStorage.getItem("cartData");
        if (!dataStr)
            return;

        try {
            const data = JSON.parse(dataStr) as { expiresAt: string, storeKey: string, items: IOrderItem[] };
            const now = new Date();
            const expiresAt = new Date(data.expiresAt);

            if (expiresAt < now) {
                localStorage.removeItem("cartData");
                return;
            }

            this._order.Items = (data.items || []).map(this._withoutCouponDiscounts);

            const newStoreKey = this._context.getProperty("targetStoreKey");
            if (newStoreKey && data.storeKey !== newStoreKey) {
                const result = await this.checkCartOkayOnStoreChangeAsync(newStoreKey, loyaltyProvider);
                if (!result.success)
                    this._order.Items = data.items = this._order.Items.filter(x => !result.invalidRecIds.includes(x.ItemRecId));
            }

            if (data.items.length === 0)
                localStorage.removeItem("cartData");

            this._eventBus.emit(EventTypes.CART_CHANGED);
            if (!this._data.storeKey && !newStoreKey)
                this._eventBus.emit(EventTypes.STORE_CHANGED, data.storeKey, loyaltyProvider);
        } catch (e) {
            console.warn("Error loading cart:", e);
            localStorage.removeItem("cartData");
        }
    }

    private _clearCart(): void {
        const dataStr = localStorage.getItem("cartData");
        if (!dataStr)
            return;

        localStorage.removeItem("cartData");
        this._order.Items = [];
        this._eventBus.emit(EventTypes.CART_CHANGED);
    }

    public async checkCartOkayOnStoreChangeAsync(selectedStoreKey: string, loyaltyProvider: ILoyaltyProvider): Promise<{ success: boolean, invalidRecIds: number[] }> {
        const newStoreData = await this._data.getStoreDataAsync(selectedStoreKey, loyaltyProvider);

        if (!newStoreData)
            return { success: true, invalidRecIds: [] };

        const invalidRecIds = this._validateAllOrderItemsForStore(newStoreData);

        return { success: invalidRecIds.length === 0, invalidRecIds };
    }

    private _validateAllOrderItemsForStore(data: IData): number[] {
        const orderTypeId = this._order.OrderTypeID;
        const invalidRecIds: number[] = [];
        for (const orderItem of this._order.Items) {
            const menuItem = data.Items.find(x => x.ItemId === orderItem.ItemId);

            if (!menuItem
                || !OnlineOrderingUtil.isItemValidForOrderType(menuItem, orderTypeId)
                || !this._isItemSizeOfferedForStore(menuItem, orderItem, data)
                || !this._isItemModifierCountValidForStore(orderItem, data)
            ) {
                invalidRecIds.push(orderItem.ItemRecId);
            }
        }
        return Array.from(new Set(invalidRecIds));
    }

    // A hidden size stays valid only while a coupon offered by the new store still qualifies the line.
    private _isItemSizeOfferedForStore(menuItem: IDataItem, orderItem: IOrderItem, data: IData): boolean {
        const orderTypeId = this._order.OrderTypeID;
        if (OnlineOrderingUtil.ItemHasSize(menuItem, orderTypeId, orderItem.SizeId))
            return true;

        return OnlineOrderingUtil.ItemHasSize(menuItem, orderTypeId, orderItem.SizeId, true)
            && OnlineOrderingUtil.IsItemSizeQualifiedByLinkedCoupon(this._order.Coupons ?? [], this._order.ItemsCoupons ?? [], data.Coupons ?? [], orderItem);
    }

    public async getCouponMealDealPrice(coupon: IDataCoupon): Promise<number> {
        if (this._couponService.isStoreSupported() && (await this._couponService.isCouponSupportedAsync({ couponId: coupon.CouponId }))) {
            if (this._context.getProperty("developmentMode"))
                console.log(this._newCouponCodeMsg);

            return this._couponService.getMealDealPrice(coupon);
        }
        else {
            if (this._context.getProperty("developmentMode"))
                console.log(this._legacyCouponCodeMsg);
            return await this._coupon.getCouponMealDealPrice(this._order, coupon);
        }
    }

    private _isItemModifierCountValidForStore(orderItem: IOrderItem, data: IData): boolean {
        const menuItem = data.Items.find(x => x.ItemId === orderItem.ItemId);
        if (!menuItem)
            return false;

        if (!this._areModifiersAvailableInStore(orderItem.Modifiers, data))
            return false;

        const modifiersClone = structuredClone(orderItem.Modifiers);
        const price = this._getItemPriceForStore(menuItem, orderItem.SizeId, modifiersClone, data).Price;
        return price >= 0;
    }

    private _areModifiersAvailableInStore(modifiers: IOrderItemModifier[], storeData: IData): boolean {
        const modifierExists = modifiers.every(mod =>
            storeData.Modifiers.some(m => m.ModifierId === mod.ModifierId)
        );
        return modifierExists;
    }

    private _getItemPriceForStore(dItem: IDataItem, sizeId: number, modifiers: IOrderItemModifier[], storeData: IData): IMenuItemPrice {
        const orderTypeId = this.getProperty("OrderTypeID");
        const orderTypePriceGroupId = OnlineOrderingUtil.getOrderType(orderTypeId, storeData.OrderTypeSubTypes)?.PriceGroupId ?? 0;

        if (orderTypePriceGroupId === 0) {
            return {
                Priced: false,
                Price: -1,
                MinPrice: -1,
                TaxIncluded: false,
                Tax: 0
            } as IMenuItemPrice;
        }

        const defaultPriceGroup = storeData.DefaultPriceGroupId;
        const nonChargeableDisplayGroups = storeData.Settings.NCDG;
        const preModifiers = storeData.PreModifiers;
        try {
            return OnlineOrderingUtil.GetItemPrice(dItem, sizeId, modifiers, orderTypePriceGroupId, defaultPriceGroup, nonChargeableDisplayGroups, preModifiers);
        } catch (e) {
            if (!(e instanceof ItemPricingUnavailableError))
                throw e;

            // No price row for this item/size at the target store -- treat it the same as "unavailable" below.
            return {
                Priced: false,
                Price: -1,
                MinPrice: -1,
                TaxIncluded: false,
                Tax: 0
            } as IMenuItemPrice;
        }
    }
}